import React, { useState } from 'react';
import { UNITS, formatEur, lineTotal, positionsTotal, unitCodeFor } from '../utils/salesPipeline';

const EMPTY_ITEM = {
    product_id: null, description: '', sub_description: '',
    quantity: 1, unit: 'Stk', unit_code: 'C62',
    unit_price: 0, discount_percent: 0, total: 0,
};

/** Editor for a deal's positions. A catalogue position keeps a `product_id` but stores its own
 *  price snapshot, so a later catalogue change never rewrites a deal. */
export default function PositionsEditor({ items, onChange, products = [], productsError = '' }) {
    const [pickerValue, setPickerValue] = useState('');

    const update = (idx, field, value) => {
        onChange(items.map((item, i) => {
            if (i !== idx) return item;
            const next = { ...item, [field]: value };
            if (field === 'unit') next.unit_code = unitCodeFor(value);
            next.total = lineTotal(next);
            return next;
        }));
    };

    const addFreeItem = () => onChange([...items, { ...EMPTY_ITEM }]);

    const addFromCatalogue = (productId) => {
        setPickerValue('');
        const product = products.find(p => p.id === productId);
        if (!product) return;
        const item = {
            ...EMPTY_ITEM,
            product_id: product.id,
            description: product.name,
            sub_description: product.sku ? `Art.-Nr. ${product.sku}` : '',
            unit: product.unit || 'Stk',
            unit_code: product.unitCode || unitCodeFor(product.unit),
            unit_price: product.listPrice ?? 0,
        };
        onChange([...items, { ...item, total: lineTotal(item) }]);
    };

    const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

    const total = positionsTotal(items);
    const catalogue = products.filter(p => p.isActive);
    const byCategory = catalogue.reduce((acc, p) => {
        const key = p.category || 'Ohne Kategorie';
        (acc[key] = acc[key] || []).push(p);
        return acc;
    }, {});

    return (
        <div className="pos-editor">
            <div className="pos-toolbar">
                <select
                    className="input pos-picker"
                    value={pickerValue}
                    onChange={(e) => addFromCatalogue(e.target.value)}
                    disabled={catalogue.length === 0}
                >
                    <option value="">
                        {catalogue.length ? '+ Produkt aus Katalog…' : 'Katalog ist leer'}
                    </option>
                    {Object.entries(byCategory).map(([category, list]) => (
                        <optgroup key={category} label={category}>
                            {list.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name}{p.sku ? ` (${p.sku})` : ''} — {formatEur(p.listPrice, { decimals: 2 })}/{p.unit}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                <button type="button" className="button button-secondary" onClick={addFreeItem}>
                    + Freie Position
                </button>
            </div>

            {productsError && <p className="pos-error">{productsError}</p>}

            {items.length === 0 ? (
                <p className="acq-hint pos-empty">
                    Noch keine Positionen. Ohne Positionen zählt das erwartete Budget als Dealwert.
                </p>
            ) : (
                <div className="pos-table-wrap">
                    <table className="pos-table">
                        <thead>
                            <tr>
                                <th>Position</th>
                                <th className="pos-num">Menge</th>
                                <th>Einheit</th>
                                <th className="pos-num">Einzelpreis</th>
                                <th className="pos-num">Rabatt</th>
                                <th className="pos-num">Summe</th>
                                <th aria-label="Entfernen"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <input
                                            type="text"
                                            className="pos-input"
                                            placeholder="Bezeichnung"
                                            value={item.description || ''}
                                            onChange={e => update(idx, 'description', e.target.value)}
                                        />
                                        <div className="pos-sub-row">
                                            {item.product_id && <span className="pos-chip">Katalog</span>}
                                            <input
                                                type="text"
                                                className="pos-input pos-input-sub"
                                                placeholder="Zusatz (optional)"
                                                value={item.sub_description || ''}
                                                onChange={e => update(idx, 'sub_description', e.target.value)}
                                            />
                                        </div>
                                    </td>
                                    <td className="pos-num">
                                        <input
                                            type="number" min={0} step="0.25"
                                            className="pos-input pos-input-num"
                                            value={item.quantity}
                                            onChange={e => update(idx, 'quantity', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="pos-input pos-input-unit"
                                            value={item.unit || 'Stk'}
                                            onChange={e => update(idx, 'unit', e.target.value)}
                                        >
                                            {UNITS.map(u => <option key={u.unit} value={u.unit}>{u.unit}</option>)}
                                        </select>
                                    </td>
                                    <td className="pos-num">
                                        <input
                                            type="number" min={0} step="0.01"
                                            className="pos-input pos-input-num"
                                            value={item.unit_price}
                                            onChange={e => update(idx, 'unit_price', e.target.value)}
                                        />
                                    </td>
                                    <td className="pos-num">
                                        <input
                                            type="number" min={0} max={100} step="1"
                                            className="pos-input pos-input-num pos-input-narrow"
                                            value={item.discount_percent || 0}
                                            onChange={e => update(idx, 'discount_percent', e.target.value)}
                                        />
                                    </td>
                                    <td className="pos-num pos-total">{formatEur(lineTotal(item), { decimals: 2 })}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="pos-del"
                                            onClick={() => removeItem(idx)}
                                            aria-label="Position entfernen"
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="pos-sum-label">Dealwert netto</td>
                                <td className="pos-num pos-sum">{formatEur(total, { decimals: 2 })}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
